import React, { useMemo } from 'react';
import { useEditor, Node } from '@splashthat/splash-cms-engine';
import PropTypes from 'prop-types';

import TouchpointContainer from 'common/components/TouchpointEditor/components/TouchpointContainer';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import BreakpointPreviewer from 'common/components/TouchpointEditor/components/BreakpointPreviewer';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { transformStructureDataForEditor } from 'common/compounds/VirtualEventRenderer/states/lib/transformStructureDataForEditor';
import TermsAndConditionsNotification from 'common/compounds/VirtualEventRenderer/components/TermsAndConditionsNotification';

import {
    NODE_ID_PAGE_CONTAINER,
    NODE_ID_CONTENT_CONTAINER,
    NODE_ID_LOGO,
    NODE_ID_EVENT_TITLE,
    NODE_ID_COUNTDOWN_TEXT,
    NODE_ID_CHECK_IN_BUTTON,
    NODE_ID_FOOTER_CONTAINER,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_PAGE_CONTAINER,
    NODE_DISPLAY_NAME_CONTENT_CONTAINER,
    NODE_DISPLAY_NAME_LOGO,
    NODE_DISPLAY_NAME_EVENT_TITLE,
    NODE_DISPLAY_NAME_COUNTDOWN_TEXT,
    NODE_DISPLAY_NAME_CHECK_IN_BUTTON,
    NODE_DISPLAY_NAME_FOOTER_CONTAINER,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_HEADER,
    NODE_ID_HEADER,
    NODE_ID_CUSTOM_BUTTON_TWO,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
} from './constants';
import EditorTools from '../components/sidebar';
import {
    VirtualGuestExperienceContainer,
    FooterBlockContainer,
    LeftLinkContainer,
    RightLinkContainer,
} from '../../components/Common.styles';
import PageContainer from '../../components/nodes/Block/SubTypes/PageContainer';
import Header from '../../components/nodes/Block/SubTypes/Header';
import ContentContainer from '../../components/nodes/Block/SubTypes/ContentContainer';
import FooterContainer from '../../components/nodes/Block/SubTypes/FooterContainer';
import EventTitleHeadline from '../../components/nodes/Headline/SubTypes/EventTitleHeadline';
import ViewEventPageLink from '../../components/nodes/Link/SubTypes/ViewEventPageLink';
import ContactTheOrganizer from '../../components/nodes/Link/SubTypes/ContactTheOrganizer';
import ContentLogo from '../../components/nodes/Logo/SubTypes/ContentLogo';
import CustomButtonLight from '../../components/nodes/Button/SubTypes/CustomButtonLight';
import CountdownText from '../components/nodes/Paragraph/SubTypes/CountdownText';
import CheckInButton from '../components/nodes/Button/SubTypes/CheckInButton';

export default function CheckIn({
    structure,
    currentPageState,
    onCheckInClick,
    isTermsAndConditionsNotificationEnabled,
}) {
    const editor = useEditor((state) => ({
        editing: state.editing,
    }));

    const { event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });

    const checkInPageStructure = useMemo(
        () => transformStructureDataForEditor(structure, currentPageState),
        [structure, currentPageState],
    );

    return (
        <TouchpointContainer editing={editor.editing}>
            <BreakpointPreviewer enabled={shouldPreview}>
                <>
                    <VirtualGuestExperienceContainer $editing={editor.editing} $previewing={shouldPreview}>
                        <Node
                            is={PageContainer}
                            id={NODE_ID_PAGE_CONTAINER}
                            displayName={NODE_DISPLAY_NAME_PAGE_CONTAINER}
                            {...checkInPageStructure[NODE_ID_PAGE_CONTAINER]}
                        >
                            <Node
                                is={Header}
                                id={NODE_ID_HEADER}
                                displayName={NODE_DISPLAY_NAME_HEADER}
                                {...checkInPageStructure[NODE_ID_HEADER]}
                            />
                            <Node
                                is={ContentContainer}
                                id={NODE_ID_CONTENT_CONTAINER}
                                displayName={NODE_DISPLAY_NAME_CONTENT_CONTAINER}
                                {...checkInPageStructure[NODE_ID_CONTENT_CONTAINER]}
                            >
                                <Node
                                    is={ContentLogo}
                                    id={NODE_ID_LOGO}
                                    displayName={NODE_DISPLAY_NAME_LOGO}
                                    {...checkInPageStructure[NODE_ID_LOGO]}
                                />
                                <Node
                                    is={EventTitleHeadline}
                                    id={NODE_ID_EVENT_TITLE}
                                    displayName={NODE_DISPLAY_NAME_EVENT_TITLE}
                                    {...checkInPageStructure[NODE_ID_EVENT_TITLE]}
                                />
                                <Node
                                    is={CountdownText}
                                    id={NODE_ID_COUNTDOWN_TEXT}
                                    displayName={NODE_DISPLAY_NAME_COUNTDOWN_TEXT}
                                    {...checkInPageStructure[NODE_ID_COUNTDOWN_TEXT]}
                                />
                                <Node
                                    is={CheckInButton}
                                    id={NODE_ID_CHECK_IN_BUTTON}
                                    displayName={NODE_DISPLAY_NAME_CHECK_IN_BUTTON}
                                    onClick={onCheckInClick}
                                    {...checkInPageStructure[NODE_ID_CHECK_IN_BUTTON]}
                                />
                            </Node>
                            <Node
                                is={FooterContainer}
                                id={NODE_ID_FOOTER_CONTAINER}
                                displayName={NODE_DISPLAY_NAME_FOOTER_CONTAINER}
                                {...checkInPageStructure[NODE_ID_FOOTER_CONTAINER]}
                            >
                                <FooterBlockContainer>
                                    <LeftLinkContainer>
                                        <li>
                                            <Node
                                                is={ContactTheOrganizer}
                                                id={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                                                displayName={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                                                {...checkInPageStructure[NODE_ID_CONTACT_ORGANIZER_BUTTON]}
                                            />
                                        </li>
                                        <li>
                                            <Node
                                                is={ViewEventPageLink}
                                                id={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                                                displayName={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                                                {...checkInPageStructure[NODE_ID_VISIT_EVENT_PAGE_BUTTON]}
                                            />
                                        </li>
                                    </LeftLinkContainer>
                                    <RightLinkContainer>
                                        <Node
                                            is={CustomButtonLight}
                                            id={NODE_ID_CUSTOM_BUTTON}
                                            displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                                            {...checkInPageStructure[NODE_ID_CUSTOM_BUTTON]}
                                        />
                                        <Node
                                            is={CustomButtonLight}
                                            id={NODE_ID_CUSTOM_BUTTON_TWO}
                                            displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                                            {...checkInPageStructure[NODE_ID_CUSTOM_BUTTON_TWO]}
                                        />
                                    </RightLinkContainer>
                                </FooterBlockContainer>
                            </Node>
                        </Node>
                    </VirtualGuestExperienceContainer>
                    {isTermsAndConditionsNotificationEnabled && !editor.editing && <TermsAndConditionsNotification />}
                </>
            </BreakpointPreviewer>
            {editor.editing && <EditorTools />}
        </TouchpointContainer>
    );
}

CheckIn.propTypes = {
    structure: PropTypes.object,
    currentPageState: PropTypes.string.isRequired,
    onCheckInClick: PropTypes.func,
    isTermsAndConditionsNotificationEnabled: PropTypes.bool,
};

CheckIn.defaultProps = {
    structure: null,
    onCheckInClick: undefined,
    isTermsAndConditionsNotificationEnabled: false,
};
